import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Drawer,
  Descriptions,
  Badge,
  Dropdown,
  Menu,
  Tabs,
  InputNumber,
  Empty,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  RedoOutlined,
  StopOutlined,
  MoreOutlined,
  ContainerOutlined,
  AppstoreOutlined,
  UserOutlined,
  ClockCircleOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import lightappService from '../../services/lightappService';
import { serviceStatusColor } from '../../../shared/components/ServiceStatusTag';

const { Option } = Select;
const { TextArea } = Input;

const Services = () => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [containers, setContainers] = useState([]);
  const [containersLoading, setContainersLoading] = useState(false);
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [createForm] = Form.useForm();
  const [stats, setStats] = useState({
    total: 0,
    running: 0,
    stopped: 0,
    pending: 0,
    error: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    deployment_type: null,
    endpoint_id: null,
  });

  useEffect(() => {
    fetchServices();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchServices, 30000);
    return () => clearInterval(interval);
  }, [filters]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await lightappService.getServices(filters);
      if (response.success) {
        const data = response.data?.list || response.data || [];
        setServices(data);
        calculateStats(data);
      } else {
        message.error(response.error || 'Failed to fetch services');
      }
    } catch (error) {
      message.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total = data.length;
    const running = data.filter((s) => s.status === 'running').length;
    const stopped = data.filter((s) => s.status === 'stopped').length;
    const pending = data.filter((s) => s.status === 'pending').length;
    const error = data.filter(
      (s) => s.status === 'error' || s.status === 'failed'
    ).length;
    setStats({ total, running, stopped, pending, error });
  };

  const handleAction = async (id, action, confirmMessage) => {
    Modal.confirm({
      title: 'Confirm Action',
      content: confirmMessage,
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: action }));
        try {
          const response = await lightappService.serviceAction(id, action);
          if (response.success) {
            message.success(`Service ${action} successful`);
            fetchServices();
          } else {
            message.error(response.error || `Failed to ${action} service`);
          }
        } catch (error) {
          message.error(`Failed to ${action} service`);
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  const handleReinstall = async (id) => {
    Modal.confirm({
      title: 'Reinstall Service',
      content: 'This will completely reinstall the service. Continue?',
      okType: 'danger',
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: 'reinstall' }));
        try {
          const response = await lightappService.reinstallService(id);
          if (response.success) {
            message.success('Service reinstall initiated');
            fetchServices();
          } else {
            message.error(response.error || 'Failed to reinstall service');
          }
        } catch (error) {
          message.error('Failed to reinstall service');
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  const handleTerminate = async (id) => {
    Modal.confirm({
      title: 'Terminate Service',
      content: 'This will permanently delete the service and all its data. This action cannot be undone!',
      okType: 'danger',
      okText: 'Terminate',
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: 'terminate' }));
        try {
          const response = await lightappService.terminateService(id);
          if (response.success) {
            message.success('Service terminated');
            fetchServices();
          } else {
            message.error(response.error || 'Failed to terminate service');
          }
        } catch (error) {
          message.error('Failed to terminate service');
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  const handleSuspend = async (id) => {
    Modal.confirm({
      title: '暂停服务',
      content: '暂停后服务将停止运行，用户无法启动。确认继续？',
      okType: 'danger',
      okText: '暂停',
      cancelText: '取消',
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: 'suspend' }));
        try {
          const response = await lightappService.suspendService(id);
          if (response.success) {
            message.success('服务已暂停');
            fetchServices();
          } else {
            message.error(response.error || '暂停服务失败');
          }
        } catch (error) {
          message.error('暂停服务失败');
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  const handleUnsuspend = async (id) => {
    Modal.confirm({
      title: '恢复服务',
      content: '恢复后服务将重新启动。确认继续？',
      okText: '恢复',
      cancelText: '取消',
      onOk: async () => {
        setActionLoading((prev) => ({ ...prev, [id]: 'unsuspend' }));
        try {
          const response = await lightappService.unsuspendService(id);
          if (response.success) {
            message.success('服务已恢复');
            fetchServices();
          } else {
            message.error(response.error || '恢复服务失败');
          }
        } catch (error) {
          message.error('恢复服务失败');
        } finally {
          setActionLoading((prev) => ({ ...prev, [id]: null }));
        }
      },
    });
  };

  const handleViewDetails = async (record) => {
    setSelectedService(record);
    setContainers([]);
    setLogs('');
    setDetailVisible(true);
  };

  const isStack = (service) =>
    service &&
    (service.deployment_type === 'stack' || service.instance_type === 'stack');

  const fetchContainers = async (id) => {
    setContainersLoading(true);
    try {
      const response = await lightappService.getServiceContainers(id);
      if (response.success) {
        setContainers(response.data?.list || response.data || []);
      } else {
        message.error(response.error || 'Failed to load containers');
      }
    } catch (error) {
      message.error('Failed to load containers');
    } finally {
      setContainersLoading(false);
    }
  };

  const handleSyncContainers = async (id) => {
    setContainersLoading(true);
    try {
      const response = await lightappService.syncServiceContainers(id);
      if (response.success) {
        message.success('Containers synced');
        fetchContainers(id);
      } else {
        message.error(response.error || 'Failed to sync containers');
        setContainersLoading(false);
      }
    } catch (error) {
      message.error('Failed to sync containers');
      setContainersLoading(false);
    }
  };

  const handleContainerAction = async (serviceId, containerId, action) => {
    try {
      const response = await lightappService.containerAction(serviceId, containerId, action);
      if (response.success) {
        message.success(`Container ${action} successful`);
        fetchContainers(serviceId);
      } else {
        message.error(response.error || `Failed to ${action} container`);
      }
    } catch (error) {
      message.error(`Failed to ${action} container`);
    }
  };

  const fetchLogs = async (id) => {
    setLogsLoading(true);
    try {
      const response = await lightappService.getServiceLogs(id, { tail: 500 });
      if (response.success) {
        setLogs(response.data?.logs ?? response.data ?? '');
      } else {
        message.error(response.error || 'Failed to load logs');
      }
    } catch (error) {
      message.error('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDrawerTabChange = (key) => {
    if (!selectedService) return;
    if (key === 'containers' && containers.length === 0) {
      fetchContainers(selectedService.id);
    } else if (key === 'logs') {
      fetchLogs(selectedService.id);
    }
  };

  const openCreateModal = async () => {
    createForm.resetFields();
    setCreateVisible(true);
    try {
      const response = await lightappService.getProducts({ page_size: 200 });
      if (response.success) {
        setProducts(response.data?.list || response.data || []);
      }
    } catch (error) {
      // ignore, product select will be empty
    }
  };

  const handleCreateService = async () => {
    try {
      const values = await createForm.validateFields();
      const product = products.find((p) => p.id === values.product_id);
      const templateId = product?.template_id || product?.template?.id;
      if (!templateId) {
        message.error('Selected product has no template configured');
        return;
      }
      setCreateLoading(true);
      const payload = {
        user_id: values.user_id,
        product_id: values.product_id,
        template_id: templateId,
        name: values.name,
      };
      if (values.endpoint_id) {
        payload.endpoint_id = values.endpoint_id;
      }
      const response = await lightappService.createService(payload);
      if (response.success) {
        message.success('Service created');
        setCreateVisible(false);
        fetchServices();
      } else {
        message.error(response.error || 'Failed to create service');
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error('Failed to create service');
    } finally {
      setCreateLoading(false);
    }
  };

  // Use the shared single-source-of-truth status color map (see
  // src/shared/components/ServiceStatusTag) so admin/user surfaces stay in sync.
  const getStatusColor = serviceStatusColor;

  const getActionMenu = (record) => (
    <Menu>
      <Menu.Item
        key="start"
        icon={<PlayCircleOutlined />}
        onClick={() => handleAction(record.id, 'start', 'Start this service?')}
        disabled={record.status === 'running'}
      >
        Start
      </Menu.Item>
      <Menu.Item
        key="stop"
        icon={<PauseCircleOutlined />}
        onClick={() => handleAction(record.id, 'stop', 'Stop this service?')}
        disabled={record.status === 'stopped'}
      >
        Stop
      </Menu.Item>
      <Menu.Item
        key="restart"
        icon={<ReloadOutlined />}
        onClick={() => handleAction(record.id, 'restart', 'Restart this service?')}
      >
        Restart
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="reinstall"
        icon={<RedoOutlined />}
        onClick={() => handleReinstall(record.id)}
      >
        Reinstall
      </Menu.Item>
      <Menu.Divider />
      {record.status !== 'suspended' ? (
        <Menu.Item
          key="suspend"
          icon={<LockOutlined />}
          onClick={() => handleSuspend(record.id)}
          disabled={record.status === 'suspended'}
        >
          暂停
        </Menu.Item>
      ) : (
        <Menu.Item
          key="unsuspend"
          icon={<UnlockOutlined />}
          onClick={() => handleUnsuspend(record.id)}
        >
          恢复
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item
        key="terminate"
        danger
        icon={<DeleteOutlined />}
        onClick={() => handleTerminate(record.id)}
      >
        Terminate
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Service',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size="small">
          <Space>
            {record.deployment_type === 'container' ? (
              <ContainerOutlined style={{ fontSize: 16 }} />
            ) : (
              <AppstoreOutlined style={{ fontSize: 16 }} />
            )}
            <span style={{ fontWeight: 500 }}>{text}</span>
          </Space>
          <span style={{ fontSize: '12px', color: '#999' }}>ID: {record.id}</span>
        </Space>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user_id',
      key: 'user_id',
      render: (userId) => (
        <Space>
          <UserOutlined />
          <span>{userId || 'System'}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'deployment_type',
      key: 'deployment_type',
      filters: [
        { text: 'Container', value: 'container' },
        { text: 'Stack', value: 'stack' },
      ],
      onFilter: (value, record) => record.deployment_type === value,
      render: (type) => (
        <Tag color={type === 'container' ? 'blue' : 'purple'}>
          {type === 'container' ? 'Container' : 'Stack'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Running', value: 'running' },
        { text: 'Stopped', value: 'stopped' },
        { text: 'Pending', value: 'pending' },
        { text: 'Error', value: 'error' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Badge
          status={getStatusColor(status) === 'success' ? 'processing' : 'default'}
          text={
            <Tag color={getStatusColor(status)}>
              {status?.toUpperCase() || 'UNKNOWN'}
            </Tag>
          }
        />
      ),
    },
    {
      title: 'Resources',
      key: 'resources',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <span style={{ fontSize: '12px' }}>
            Mem: {record.memory_limit || 512}MB
          </span>
          <span style={{ fontSize: '12px' }}>
            CPU: {record.cpu_limit || 1} cores
          </span>
        </Space>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint_id',
      key: 'endpoint_id',
      render: (id) => <Tag>Node-{id}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <Space>
          <ClockCircleOutlined />
          {new Date(date).toLocaleDateString()}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            Details
          </Button>
          <Dropdown
            overlay={getActionMenu(record)}
            trigger={['click']}
            disabled={actionLoading[record.id]}
          >
            <Button
              size="small"
              icon={<MoreOutlined />}
              loading={actionLoading[record.id]}
            />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card>
            <Statistic title="服务总数" value={stats.total} />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="运行中"
              value={stats.running}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic title="已停止" value={stats.stopped} />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="等待中"
              value={stats.pending}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="异常"
              value={stats.error}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="服务管理"
        extra={
          <Space>
            <Select
              placeholder="Filter by Status"
              style={{ width: 150 }}
              allowClear
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Option value="running">Running</Option>
              <Option value="stopped">Stopped</Option>
              <Option value="pending">Pending</Option>
              <Option value="error">Error</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchServices} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Create Service
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={services}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1300 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No services found. Click “Create Service” to deploy one."
              />
            ),
          }}
        />
      </Card>

      <Drawer
        title="服务详情"
        placement="right"
        width={720}
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
      >
        {selectedService && (
          <Tabs defaultActiveKey="1" onChange={handleDrawerTabChange}>
            <Tabs.TabPane tab="Basic Info" key="1">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Service ID">
                  {selectedService.id}
                </Descriptions.Item>
                <Descriptions.Item label="Name">
                  {selectedService.name}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag
                    color={
                      selectedService.deployment_type === 'container'
                        ? 'blue'
                        : 'purple'
                    }
                  >
                    {selectedService.deployment_type}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(selectedService.status)}>
                    {selectedService.status?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="User ID">
                  {selectedService.user_id || 'System'}
                </Descriptions.Item>
                <Descriptions.Item label="Endpoint">
                  Node-{selectedService.endpoint_id}
                </Descriptions.Item>
                <Descriptions.Item label="Memory Limit">
                  {selectedService.memory_limit} MB
                </Descriptions.Item>
                <Descriptions.Item label="CPU Limit">
                  {selectedService.cpu_limit} cores
                </Descriptions.Item>
                <Descriptions.Item label="Disk Size">
                  {selectedService.disk_size} GB
                </Descriptions.Item>
                <Descriptions.Item label="Billing Mode">
                  <Tag>{selectedService.billing_mode || 'fixed'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {new Date(selectedService.created_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Updated At">
                  {new Date(selectedService.updated_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Compose/Config" key="2">
              {selectedService.compose_content ? (
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 4,
                    maxHeight: 600,
                    overflow: 'auto',
                  }}
                >
                  {selectedService.compose_content}
                </pre>
              ) : (
                <p>No compose content available</p>
              )}
            </Tabs.TabPane>

            <Tabs.TabPane tab="Containers" key="containers">
              {isStack(selectedService) ? (
                <>
                  <div style={{ marginBottom: 12, textAlign: 'right' }}>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => handleSyncContainers(selectedService.id)}
                      loading={containersLoading}
                    >
                      Sync Containers
                    </Button>
                  </div>
                  <Table
                    size="small"
                    rowKey="id"
                    loading={containersLoading}
                    dataSource={containers}
                    pagination={false}
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name' },
                      {
                        title: 'Status',
                        key: 'status',
                        render: (_, c) => (
                          <Tag color={getStatusColor(c.live_status || c.status)}>
                            {(c.live_status || c.status || 'unknown').toUpperCase()}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        render: (_, c) => (
                          <Space>
                            <Button
                              type="link"
                              size="small"
                              icon={<PlayCircleOutlined />}
                              onClick={() =>
                                handleContainerAction(selectedService.id, c.id, 'start')
                              }
                            >
                              Start
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              icon={<PauseCircleOutlined />}
                              onClick={() =>
                                handleContainerAction(selectedService.id, c.id, 'stop')
                              }
                            >
                              Stop
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              icon={<ReloadOutlined />}
                              onClick={() =>
                                handleContainerAction(selectedService.id, c.id, 'restart')
                              }
                            >
                              Restart
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ) : (
                <Empty description="Containers are only available for stack services" />
              )}
            </Tabs.TabPane>

            <Tabs.TabPane tab="Logs" key="logs">
              <div style={{ marginBottom: 12, textAlign: 'right' }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchLogs(selectedService.id)}
                  loading={logsLoading}
                >
                  Refresh Logs
                </Button>
              </div>
              <Spin spinning={logsLoading}>
                <pre
                  style={{
                    background: '#1e1e1e',
                    color: '#d4d4d4',
                    padding: 16,
                    borderRadius: 4,
                    maxHeight: 600,
                    overflow: 'auto',
                    fontSize: 12,
                    minHeight: 120,
                  }}
                >
                  {logs || 'No logs available. Click "Refresh Logs" to load.'}
                </pre>
              </Spin>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Actions" key="3">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  block
                  onClick={() =>
                    handleAction(
                      selectedService.id,
                      'start',
                      'Start this service?'
                    )
                  }
                  disabled={selectedService.status === 'running'}
                >
                  Start Service
                </Button>
                <Button
                  icon={<PauseCircleOutlined />}
                  block
                  onClick={() =>
                    handleAction(selectedService.id, 'stop', 'Stop this service?')
                  }
                  disabled={selectedService.status === 'stopped'}
                >
                  Stop Service
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  block
                  onClick={() =>
                    handleAction(
                      selectedService.id,
                      'restart',
                      'Restart this service?'
                    )
                  }
                >
                  Restart Service
                </Button>
                <Button
                  icon={<RedoOutlined />}
                  block
                  onClick={() => handleReinstall(selectedService.id)}
                >
                  Reinstall Service
                </Button>
                {selectedService.status !== 'suspended' ? (
                  <Button
                    icon={<LockOutlined />}
                    block
                    onClick={() => handleSuspend(selectedService.id)}
                  >
                    暂停服务
                  </Button>
                ) : (
                  <Button
                    icon={<UnlockOutlined />}
                    block
                    type="primary"
                    onClick={() => handleUnsuspend(selectedService.id)}
                  >
                    恢复服务
                  </Button>
                )}
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  block
                  onClick={() => handleTerminate(selectedService.id)}
                >
                  Terminate Service
                </Button>
              </Space>
            </Tabs.TabPane>
          </Tabs>
        )}
      </Drawer>

      <Modal
        title="创建服务"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={handleCreateService}
        confirmLoading={createLoading}
        okText="Create"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="user_id"
            label="User ID"
            rules={[{ required: true, message: 'Please enter the target user ID' }]}
            extra="The user who will own this service instance."
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="e.g., 42" />
          </Form.Item>
          <Form.Item
            name="product_id"
            label="Product / Plan"
            rules={[{ required: true, message: 'Please select a product' }]}
          >
            <Select
              placeholder="Select a product"
              showSearch
              optionFilterProp="children"
            >
              {products.map((p) => (
                <Option key={p.id} value={p.id}>
                  {(p.display_name || p.name)}
                  {p.template ? ` (${p.template.name})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="Service Name"
            extra="Optional. A unique name is generated if left blank."
          >
            <Input placeholder="my-service" />
          </Form.Item>
          <Form.Item
            name="endpoint_id"
            label="Endpoint ID"
            extra="Optional. Uses the product's default or the default endpoint if blank."
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="e.g., 4" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Services;
